import { useId } from 'react';
import { Box, Image } from '@chakra-ui/react';
import { DOMAIN_HEX } from '../utils/domains';

// ─── Duotone helpers ──────────────────────────────────────────────────────────

type Rgb = [number, number, number];

function hexToRgb01(hex: string): Rgb {
  return [
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  ];
}

/** Neutral-dark shadow: matches the app base background #0b0b10 */
const DARK_SHADOW: Rgb = [0.043, 0.043, 0.063];

/**
 * Compute the shadow/highlight colour pair for a duotone.
 *
 * - 1 domain  → shadow = near-black, highlight = domain colour
 * - 2 domains → shadow = dark tint of domain[0], highlight = domain[1]
 */
function duotoneColors(domains: string[]): { shadow: Rgb; highlight: Rgb } | null {
  const hexes = domains.slice(0, 2).map((d) => DOMAIN_HEX[d]).filter(Boolean);
  if (hexes.length === 0) return null;

  if (hexes.length === 1) {
    return { shadow: DARK_SHADOW, highlight: hexToRgb01(hexes[0]) };
  }

  const [r1, g1, b1] = hexToRgb01(hexes[0]);
  const [r2, g2, b2] = hexToRgb01(hexes[1]);
  return {
    shadow:    [r1 * 0.14, g1 * 0.14, b1 * 0.14],
    highlight: [r2, g2, b2],
  };
}

// ─── SVG filter component ─────────────────────────────────────────────────────

function DuotoneFilterDef({
  id,
  shadow,
  highlight,
}: {
  id: string;
  shadow: Rgb;
  highlight: Rgb;
}) {
  return (
    <svg
      width={0}
      height={0}
      style={{ position: 'absolute', pointerEvents: 'none' }}
      aria-hidden
    >
      <defs>
        <filter id={id} colorInterpolationFilters="sRGB" x="0%" y="0%" width="100%" height="100%">
          {/* Step 1 — desaturate to greyscale */}
          <feColorMatrix type="saturate" values="0" />
          {/* Step 2 — remap grey to shadow→highlight colour range */}
          <feComponentTransfer>
            <feFuncR type="table" tableValues={`${shadow[0]} ${highlight[0]}`} />
            <feFuncG type="table" tableValues={`${shadow[1]} ${highlight[1]}`} />
            <feFuncB type="table" tableValues={`${shadow[2]} ${highlight[2]}`} />
          </feComponentTransfer>
        </filter>
      </defs>
    </svg>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export interface BlurredArtBackgroundProps {
  /** Image URL to use as the source (thumbnail preferred). */
  src?: string;
  /**
   * Domain names (e.g. `['Body', 'Chaos']`) used to derive the duotone palette.
   * Pass an empty array or omit to skip duotone and just blur.
   */
  domains?: string[];
  /**
   * Opacity of the blurred layer.
   * @default 0.10
   */
  opacity?: number;
  /**
   * CSS blur radius.
   * @default '8px'
   */
  blur?: string;
  /**
   * Position of the source image inside the background layer.
   * @default 'center'
   */
  objectPosition?: string;
  /**
   * Image scale used to extend the blurred layer beyond its bounds.
   * Use 1 for no overscan, 1.1 for 110%, etc.
   * @default 1.1
   */
  scale?: number;
  /** zIndex of the absolute layer. @default 0 */
  zIndex?: number | 'auto';
  /** First-screen art. Eager + high priority so it can be the LCP image. */
  priority?: boolean;
}

/**
 * Absolutely-positioned blurred + optionally duotone-tinted art layer.
 * Place inside a `position: relative` container with `overflow: hidden`.
 * The image extends 5% beyond bounds to hide CSS-blur's soft edges.
 */
export function BlurredArtBackground({
  src,
  domains,
  opacity = 0.10,
  blur = '12px',
  objectPosition = 'center',
  scale = 1.1,
  zIndex = 0,
  priority = false,
}: BlurredArtBackgroundProps) {
  const rawId   = useId();
  const filterId = `bad-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;

  if (!src) return null;

  const duotone = domains && domains.length > 0 ? duotoneColors(domains) : null;

  const cssFilter = duotone
    ? `url(#${filterId}) blur(${blur})`
    : `blur(${blur})`;
  const overscan = `${((scale - 1) * 50).toFixed(3)}%`;
  const scaledSize = `${(scale * 100).toFixed(3)}%`;

  return (
    <Box
      position="absolute"
      inset={0}
      overflow="hidden"
      borderRadius="inherit"
      pointerEvents="none"
      zIndex={zIndex}
      aria-hidden
    >
      {duotone && (
        <DuotoneFilterDef
          id={filterId}
          shadow={duotone.shadow}
          highlight={duotone.highlight}
        />
      )}
      <Image
        src={src}
        alt=""
        position="absolute"
        top={`-${overscan}`}
        left={`-${overscan}`}
        w={scaledSize}
        h={scaledSize}
        objectFit="cover"
        objectPosition={objectPosition}
        style={{ filter: cssFilter, opacity, transform: 'translateZ(0)' }}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        draggable={false}
      />
    </Box>
  );
}
